import type { ChatState, Snowflake } from '../stores/state.js';

/**
 * O texto do compositor → o que vai para a API: `@usuario` vira `<@id>`,
 * `#canal` vira `<#id>`, `:emoji:` vira `<:emoji:id>` (ou o caractere, se é
 * um emoji comum) e as menções escolhidas no autocompletar (cargos, nomes
 * com espaço) viram o código delas. Código (`…` e ```…```) fica como está.
 */
export type ContextoSaida = {
  /** Escolhas do autocompletar: texto inserido → código. */
  escolhidas: Map<string, string>;
  pessoas: Array<{ id: string; username: string }>;
  canais: Array<{ id: string; name?: string | null }>;
  emojis: Array<{ id: string; name: string; animated?: boolean }>;
  /** `:atalho:` → emoji (ex.: `thumbsup` → 👍). */
  atalhos?: Map<string, string>;
};

const CODIGO = /(```[\s\S]*?```|`[^`\n]*`)/;

function trocarFora(texto: string, troca: (pedaco: string) => string): string {
  return texto
    .split(CODIGO)
    .map((pedaco, indice) => (indice % 2 === 1 ? pedaco : troca(pedaco)))
    .join('');
}

function escaparRegex(texto: string): string {
  return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function paraEnvio(texto: string, contexto: ContextoSaida): string {
  const pessoas = new Map(
    contexto.pessoas.map((pessoa) => [
      pessoa.username.toLowerCase(),
      pessoa.id,
    ]),
  );
  const canais = new Map(
    contexto.canais
      .filter((canal) => canal.name)
      .map((canal) => [String(canal.name).toLowerCase(), canal.id]),
  );
  const emojis = new Map(
    contexto.emojis.map((emoji) => [emoji.name.toLowerCase(), emoji]),
  );
  const escolhidas = [...contexto.escolhidas.entries()].sort(
    (a, b) => b[0].length - a[0].length,
  );
  return trocarFora(texto, (pedaco) => {
    let resultado = pedaco;
    for (const [inserido, codigo] of escolhidas) {
      resultado = resultado.replace(
        new RegExp(`(^|\\s)${escaparRegex(inserido)}(?=$|[\\s.,!?;:)])`, 'g'),
        (_tudo, antes: string) => `${antes}${codigo}`,
      );
    }
    resultado = resultado.replace(
      /(^|\s)@([\p{L}\p{N}._]{2,32})(?=$|[\s,!?;:)])/gu,
      (tudo, antes: string, nome: string) => {
        const id = pessoas.get(nome.toLowerCase());
        return id ? `${antes}<@${id}>` : tudo;
      },
    );
    resultado = resultado.replace(
      /(^|\s)#([^\s#<>]{1,100})(?=$|[\s,!?;:)])/gu,
      (tudo, antes: string, nome: string) => {
        const id = canais.get(nome.toLowerCase());
        return id ? `${antes}<#${id}>` : tudo;
      },
    );
    resultado = resultado.replace(
      /(^|[^<\w]):([A-Za-z0-9_+-]{2,32}):(?!\d)/g,
      (tudo, antes: string, nome: string) => {
        const emoji = emojis.get(nome.toLowerCase());
        if (emoji) {
          return `${antes}<${emoji.animated ? 'a' : ''}:${emoji.name}:${emoji.id}>`;
        }
        const unicode = contexto.atalhos?.get(nome.toLowerCase());
        return unicode ? `${antes}${unicode}` : tudo;
      },
    );
    return resultado;
  });
}

/**
 * O caminho de volta, para editar: `<@id>` → `@usuario`, `<#id>` → `#canal`,
 * `<:emoji:id>` → `:emoji:` (o envio converte de novo). Menção a cargo
 * fica como código (não há como escrever o nome de volta sem ambiguidade).
 */
export function paraEdicao(
  texto: string,
  nomes: {
    pessoa: (id: string) => string | null;
    canal: (id: string) => string | null;
  },
): string {
  return trocarFora(texto, (pedaco) =>
    pedaco
      .replace(/<@!?(\d+)>/g, (tudo, id: string) => {
        const nome = nomes.pessoa(id);
        return nome && /^[\p{L}\p{N}._]{2,32}$/u.test(nome) ? `@${nome}` : tudo;
      })
      .replace(/<#(\d+)>/g, (tudo, id: string) => {
        const nome = nomes.canal(id);
        return nome && !/[\s#<>]/.test(nome) ? `#${nome}` : tudo;
      })
      .replace(/<a?:([A-Za-z0-9_]{2,32}):\d+>/g, ':$1:'),
  );
}

/** O contexto de saída de um canal: quem e o que dá para mencionar ali. */
export function contextoDoCanal(
  state: ChatState,
  channelId: Snowflake,
  escolhidas: Map<string, string>,
  atalhos?: Map<string, string>,
): ContextoSaida {
  const guildId = state.channels[channelId]?.guild_id ?? null;
  const guild = guildId ? state.guilds[guildId] : undefined;
  return {
    escolhidas,
    pessoas: guildId
      ? Object.keys(state.members[guildId] ?? {})
          .map((id) => state.users[id])
          .filter(Boolean)
      : (state.channels[channelId]?.recipients ?? []),
    canais: guildId
      ? [
          ...(state.guildChannelIds[guildId] ?? []),
          ...(state.guildThreadIds[guildId] ?? []),
        ]
          .map((id) => state.channels[id])
          .filter(Boolean)
      : [],
    emojis: guild?.emojis ?? [],
    atalhos,
  };
}

/** Converter :) em emoji (U7): fora de código, como palavra solta. */
const EMOTICONS: Array<[RegExp, string]> = [
  [/(^|\s):-?\)(?=$|\s)/g, '$1🙂'],
  [/(^|\s):-?D(?=$|\s)/g, '$1😄'],
  [/(^|\s):-?\((?=$|\s)/g, '$1🙁'],
  [/(^|\s);-?\)(?=$|\s)/g, '$1😉'],
  [/(^|\s):-?[pP](?=$|\s)/g, '$1😛'],
  [/(^|\s):-?[oO](?=$|\s)/g, '$1😮'],
  [/(^|\s)<3(?=$|\s)/g, '$1❤️'],
  [/(^|\s):'\((?=$|\s)/g, '$1😢'],
];

export function comEmoticons(texto: string): string {
  return trocarFora(texto, (pedaco) =>
    EMOTICONS.reduce(
      (atual, [padrao, emoji]) => atual.replace(padrao, emoji),
      pedaco,
    ),
  );
}
